<template>
	<validation-observer
		tag="div"
		class="integration-builder"
	>
		<!-- Integration Form Fields with embedded sidebar -->
		<IntegrationFormFields
			:http-method="httpMethod"
			:request-url="requestUrl"
			:request-body="requestBody"
			:request-throttling-wait-time-ms="requestThrottlingWaitTimeMs"
			:custom-headers="customHeaders"
			:http-method-items="httpMethodItems"
			:user-full-permissions="userFullPermissions"
			:selected-fields="selectedFields"
			:field-usage-map="fieldUsageMap"
			:is-no-auth-credentials="isNoAuthCredentials"
			:available-fields="availableFields"
			:integration-filters="integrationFilters"
			:cassie-object-id="cassieObjectId"
			:data-type-items="dataTypeItems"
			:is-edit="isEdit"
			:object-id-column="objectIdColumn"
			:reset-incremental-value="resetIncrementalValue"
			:increment-field-items="incrementFieldItems"
			:system-integration-items="systemIntegrationItems"
			:integration-id="integrationId"
			:clone-mode="cloneMode"
			@update:httpMethod="$emit('update:httpMethod', $event)"
			@update:requestUrl="$emit('update:requestUrl', $event)"
			@update:requestBody="$emit('update:requestBody', $event)"
			@update:requestThrottlingWaitTimeMs="$emit('update:requestThrottlingWaitTimeMs', $event)"
			@update:customHeaders="$emit('update:customHeaders', $event)"
			@update:integrationId="$emit('update:integrationId', $event)"
			@field-drop="handleFieldDrop"
			@field-select="handleFieldSelect"
			@field-remove="handleFieldRemove"
			@field-filter="handleFieldFilter"
			@filter-remove="handleFilterRemove"
			@data-type-change="handleDataTypeChange"
			@update:objectIdColumn="$emit('update:objectIdColumn', $event)"
			@update:resetIncrementalValue="$emit('update:resetIncrementalValue', $event)"
		/>
	</validation-observer>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import IntegrationFormFields from './IntegrationFormFields.vue'

export default {
	name: 'integration-builder',
	components: {
		ValidationObserver,
		IntegrationFormFields
	},
	props: {
		// Data Type Props
		isEdit: Boolean,
		cassieObjectId: Number,
		objectIdColumn: String,
		resetIncrementalValue: Boolean,
		cassieObjects: Array,
		dataTypeItems: Array,

		// Integration Configuration Props
		httpMethod: String,
		requestUrl: String,
		requestBody: String,
		requestThrottlingWaitTimeMs: [String, Number],
		customHeaders: {
			type: Array,
			default: () => []
		},
		httpMethodItems: Array,

		// Field and Filter Props
		objectSelectList: {
			type: Array,
			default: () => []
		},
		integrationFilters: {
			type: Array,
			default: () => []
		},

		// Permission Props
		userFullPermissions: Boolean,

		// Authentication Props
		isNoAuthCredentials: {
			type: Boolean,
			default: false
		},

		// Clone Mode
		cloneMode: Boolean,

		// System Integration Props
		systemIntegrationItems: Array,
		integrationId: Number
	},
	data () {
		return {
			selectedFields: [...this.objectSelectList],
			fieldUsageMap: {},
			isUpdatingInternally: false
		}
	},
	computed: {
		incrementFieldItems () {
			return this.cassieObjects.find(({ cassieObjectId }) => cassieObjectId === this.cassieObjectId)?.cassieObjectIncrementalFields
		},

		availableFields () {
			if (!this.cassieObjects || !this.cassieObjectId) {
				return []
			}

			const currentObject = this.cassieObjects.find(obj => obj.cassieObjectId === this.cassieObjectId)
			if (!currentObject || !currentObject.cassieObjectFields) {
				return []
			}

			return currentObject.cassieObjectFields.map(field => ({
				fieldName: field.cassieObjectFieldName,
				displayName: field.cassieObjectFieldDisplayName || field.cassieObjectFieldName,
				typeFriendlyName: field.typeFriendlyName || field.systemTypeName || 'Text',
				dataType: field.dataType || 'string',
				isFilterable: field.isFilterable || false,
				isIncremental: field.isIncremental || false
			}))
		},

		selectedFieldObjects () {
			return this.selectedFields.map(fieldName =>
				this.availableFields.find(field => field.fieldName === fieldName)
			).filter(Boolean)
		}
	},
	watch: {
		objectSelectList: {
			handler (newValue) {
				if (!this.isUpdatingInternally) {
					this.selectedFields = [...newValue]
				}
			},
			immediate: true
		},

		selectedFields: {
			handler (newValue) {
				if (!this.isUpdatingInternally) {
					this.isUpdatingInternally = true
					this.$emit('update:objectSelectList', newValue)
					this.$nextTick(() => {
						this.isUpdatingInternally = false
						this.updateFieldUsageMap()
					})
				}
			},
			deep: true
		},

		requestBody: {
			handler () {
				if (!this.isUpdatingInternally) {
					this.updateFieldUsageMap()
				}
			}
		},

		requestUrl: {
			handler () {
				if (!this.isUpdatingInternally) {
					this.updateFieldUsageMap()
				}
			}
		},

		customHeaders: {
			handler () {
				if (!this.isUpdatingInternally) {
					this.updateFieldUsageMap()
				}
			},
			deep: true
		}
	},
	created () {
		if (this.cloneMode) {
			this.$emit('update:isEnabled', false)
		}

		this.updateFieldUsageMap()
	},
	methods: {
		handleDataTypeChange (newCassieObjectId) {
			// Prevent infinite loops during data type change
			this.isUpdatingInternally = true

			// Clear fields when data type changes
			this.selectedFields = []
			this.$emit('update:objectSelectList', [])
			this.$emit('update:filters', [])

			// Clear increment field selection and emit the change
			this.$emit('update:objectIdColumn', null)

			// Update the cassie object ID last
			this.$emit('update:cassieObjectId', newCassieObjectId)

			// Re-enable watchers after everything is updated
			this.$nextTick(() => {
				this.isUpdatingInternally = false
			})
		},

		handleFieldSelect (fieldName) {
			if (!this.selectedFields.includes(fieldName)) {
				this.selectedFields.push(fieldName)
			}
		},

		handleFieldRemove (fieldName) {
			const index = this.selectedFields.indexOf(fieldName)
			if (index > -1) {
				this.selectedFields.splice(index, 1)
			}

			// Remove associated filter
			const updatedFilters = this.integrationFilters.filter(filter => filter.fieldName !== fieldName)
			this.$emit('update:filters', updatedFilters)
		},

		handleFieldFilter (filterConfig) {
			const { fieldName, filter } = filterConfig
			let found = false

			const updatedFilters = this.integrationFilters.map(existingFilter => {
				if (existingFilter.fieldName === fieldName) {
					found = true
					return { ...existingFilter, filter }
				}
				return existingFilter
			})

			if (!found) {
				updatedFilters.push({ fieldName, filter })
			}

			this.$emit('update:filters', updatedFilters)
		},

		handleFilterRemove (fieldName) {
			const updatedFilters = this.integrationFilters.filter(filter => filter.fieldName !== fieldName)
			this.$emit('update:filters', updatedFilters)
		},

		handleFieldDrop (dropData) {
			// Pass the dropData directly to the event handler
			// The individual form components will handle the actual token insertion
			this.$emit('field-drop', dropData)
		},

		updateFieldUsageMap () {
			// Avoid updating during internal state changes
			if (this.isUpdatingInternally) {
				return
			}

			const usageMap = {}

			// Initialize all available fields with 0 usage
			this.availableFields.forEach(field => {
				usageMap[field.fieldName] = {
					total: 0,
					contexts: []
				}
			})

			// Only update usage if there's content to check
			const hasContent = this.requestBody || this.requestUrl || (this.customHeaders && this.customHeaders.length > 0)
			if (!hasContent) {
				this.fieldUsageMap = usageMap
				return
			}

			// Check usage in request body
			if (this.requestBody) {
				this.availableFields.forEach(field => {
					const token = this.generateFieldToken(field)
					const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
					const matches = (this.requestBody.match(new RegExp(escapedToken, 'g')) || []).length
					if (matches > 0) {
						usageMap[field.fieldName].total += matches
						usageMap[field.fieldName].contexts.push('Payload')
					}
				})
			}

			// Check usage in request URL
			if (this.requestUrl) {
				this.availableFields.forEach(field => {
					const token = this.generateFieldToken(field)
					const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
					const matches = (this.requestUrl.match(new RegExp(escapedToken, 'g')) || []).length
					if (matches > 0) {
						usageMap[field.fieldName].total += matches
						if (!usageMap[field.fieldName].contexts.includes('URL')) {
							usageMap[field.fieldName].contexts.push('URL')
						}
					}
				})
			}

			// Check usage in custom headers
			if (this.customHeaders && this.customHeaders.length > 0) {
				this.customHeaders.forEach(header => {
					if (header.headerValue) {
						this.availableFields.forEach(field => {
							const token = this.generateFieldToken(field)
							const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
							const matches = (header.headerValue.match(new RegExp(escapedToken, 'g')) || []).length
							if (matches > 0) {
								usageMap[field.fieldName].total += matches
								if (!usageMap[field.fieldName].contexts.includes('Header')) {
									usageMap[field.fieldName].contexts.push('Header')
								}
							}
						})
					}
				})
			}

			this.fieldUsageMap = usageMap
		},

		generateFieldToken (field) {
			// Generate clean field token from field name
			const fieldPart = field.fieldName.split('.').pop() || field.fieldName
			const cleanFieldName = fieldPart.replace(/_/g, '').toUpperCase()
			return `[PLACEHOLDER_${cleanFieldName}]`
		}
	}
}
</script>

<style lang="scss" scoped>
.integration-builder {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
